import {BundleStub, SortOrder} from "../rpc/webrpc-client.gen.ts";
import {z} from "zod/v4";
import {cleanse, NADA, objectFromEntries} from "../util/arutil.tsx";
import {TieredOrActive} from "../dispatch/tieredOrActive.tsx";
import {iconForEntityType} from "../fa.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {useVenuedWastes, VenuedBundle, venuedBundleTqo, VenuedWaste} from "../venue/overview.tsx";
import {useMemo} from "react";
import {useQueries, useQueryClient} from "@tanstack/react-query";
import {mergeUseQueriesWithInfill} from "../tq/mergeUseQueriesWithInfill.tsx";
import {usePreferences} from "../agent/usePreferences.ts";
import {InspectionStageOrSurcharge} from "../inspect/stages.tsx";
import {TieredColor} from "../util/defaultPrefs.tsx";
import {useMothership} from "../tq/tqx.tsx";

export const SupervisionSettings = z.object({
    order: TieredOrActive,
    sortOrder: SortOrder,
});
export type SupervisionSettings = z.infer<typeof SupervisionSettings>;
export const defaultSupervisionSettings: SupervisionSettings = {
    order: TieredOrActive.enum.active,
    sortOrder: SortOrder.enum.DESC,
};

export function validateSupervisionSettings(raw: Record<string, unknown>): SupervisionSettings {
    const zprv = SupervisionSettings.safeParse(raw);
    const rv = zprv.success ? zprv.data : defaultSupervisionSettings;
    return rv;
}

const supervisorFacets = ["bundle", "waste"] as const;
const rawSupervisorFacetEnum = z.enum(supervisorFacets);
export type SupervisorFacet = keyof typeof rawSupervisorFacetEnum.enum;
export const SupervisorFacet = rawSupervisorFacetEnum.default("waste");
export const supervisorFacetIcons = objectFromEntries(supervisorFacets.map(name => {
    const entry = [name, iconForEntityType(name).icon] as const;
    return entry;
}));

export function useSupervisorWastes({venueId}: { venueId: string }): InfallibleUseResult<VenuedWaste[]> {
    const vxwsTqrv = useVenuedWastes({venueId});
    const prefsTqrv = usePreferences();

    const rv = useMemo(() => {
        if (!vxwsTqrv.isSuccess) {
            return pendingUseResult;
        } else {
            const inspectionStageColorMap = prefsTqrv.data?.inspectionTierColors
                ? new Map<InspectionStageOrSurcharge, TieredColor<InspectionStageOrSurcharge>>(prefsTqrv.data.inspectionTierColors.map(rec => ([rec.tier, rec])))
                : undefined;

            const wanted = vxwsTqrv.data.filter(maybe =>
                (maybe.status?.inspectionStage && inspectionStageColorMap?.get(maybe.status.inspectionStage)?.watched));
            return successUseResult(wanted);
        }
    }, [prefsTqrv.data?.inspectionTierColors, vxwsTqrv.data, vxwsTqrv.isSuccess]);

    return rv;
}

export function useSupervisorBundles({venueId}: { venueId: string }): InfallibleUseResult<VenuedBundle[]> {
    const tqc = useQueryClient();
    const mama = useMothership();
    const vxwsTqrv = useSupervisorWastes({venueId});
    const wantedBundles = useMemo<BundleStub[] | undefined>(() => {
        if (vxwsTqrv.isSuccess) {
            const ubs = new Map<string, BundleStub>();
            for (const vxw of vxwsTqrv.data) {
                if (vxw.lastBundle) {
                    ubs.set(vxw.lastBundle.id, vxw.lastBundle);
                }
            }
            return Array.from(ubs.values());
        } else {
            return undefined;
        }
    }, [vxwsTqrv.data, vxwsTqrv.isSuccess]);
    const rv = useQueries(wantedBundles
        ? {
            queries: wantedBundles.map(stub => venuedBundleTqo({tqc, mama, args: {bundleId: stub.id}})),
            combine: mergeUseQueriesWithInfill
        }
        : {queries: NADA});
    return vxwsTqrv.isSuccess ? successUseResult(cleanse(rv.data)) : pendingUseResult;
}

